import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useCreateAssessmentMutation,
  useDeleteAssessmentMutation,
  useGetAssessmentAnalyticsQuery,
  useGetAssessmentFocusesQuery,
  useGetAssessmentStatsQuery,
  useGetAssessmentsQuery,
  useGetTeamQuery,
  useGetallStudentQuery,
  useGetallSubjectQuery,
  useUpdateAssessmentMutation,
} from "../../Api/attoDeskApi";
import { getAvatar } from "../../utils/avatar";
import { presentDateOnly, todayLocal } from "../../utils/dateOnly";
import PremiumDatePicker from "../../Components/Shared/DatePicker";
import AssessmentModalShell from "../../Components/Shared/AssessmentModalShell";
import PremiumSelect from "../../Components/PremiumSelect";
import { AreaChart, DonutChart } from "../../Components/InlineCharts";
import CreateAssessmentWizard from "./CreateAssessmentWizard";
import useUser from "../../Core/hook/useUser";
import { useConfirm, confirmPresets } from "../../Components/Confirm";

// ─────────────── types ─────────────── //
interface AssessmentRow {
  id: number;
  date: string;
  subject: string;
  title: string;
  focus: string | null;
  grade: string | null;
  max_value: number;
  student_mark: number;
  percentage: number;
  analysis: string | null;
  comment_1: string | null;
  comment_2: string | null;
  comment_3: string | null;
  student_id: number;
  staff_id: number;
  // Present on rows created via the two-step wizard (Batch 6); null on
  // legacy standalone rows. Used to gate the row-level Grade action —
  // legacy rows have no group and no roster, so no Grade button.
  assessment_group_id?: number | null;
  student: { id: number; first_name: string; last_name: string; image?: string | null } | null;
  staff: { id: number; name: string; teamImg?: string | null } | null;
}

interface Stats {
  total: number;
  this_month: number;
  above_average: number;
  below_average: number;
  avg_percentage: number;
}

// The hardcoded SUBJECTS chip array (which contained both "Math" and
// "Maths") was removed in Batch 6. Create flows use the cascade endpoint
// (CreateAssessmentWizard) and Edit uses the live `subjects_tables`
// list via useGetallSubjectQuery so no new free-text variants can be
// introduced from the UI.
const GRADES = Array.from({ length: 12 }, (_, i) => `Grade ${i + 1}`);

// Edit-mode form. Create-mode uses CreateAssessmentWizard, which owns its
// own state and never carries mark/comment fields at creation time.
const emptyForm = () => ({
  student_id: "",
  staff_id: "",
  subject_id: "" as string | number,
  subject: "",
  title: "",
  focus: "",
  grade: "",
  max_value: 100,
  student_mark: 0,
  // Local calendar date — never round-trip through UTC (see utils/dateOnly).
  date: todayLocal(),
  comment_1: "",
  comment_2: "",
  comment_3: "",
});

// ─────────────── page ─────────────── //
const Assessments: React.FC = () => {
  const navigate = useNavigate();
  const { userHasRight } = useUser();
  const canCreate = userHasRight("Assessment_Create");

  const [filters, setFilters] = useState({
    student_name: "", from: "", to: "", focus: "",
    staff_id: "", grade: "", subject: "", analysis: "", range: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const { data: statsData, refetch: refetchStats } = useGetAssessmentStatsQuery();
  const { data: focusesData } = useGetAssessmentFocusesQuery();
  const { data: listData, isFetching, refetch } = useGetAssessmentsQuery(appliedFilters);

  const { data: teamData } = useGetTeamQuery({ searchText: "" } as any);
  const { data: studentData } = useGetallStudentQuery({ searchText: "" } as any);
  // Live subjects for the Edit modal's subject picker (Create uses the
  // cascade endpoint via CreateAssessmentWizard instead).
  const { data: subjectsRaw } = useGetallSubjectQuery({ searchText: "" } as any);
  const editSubjectList = useMemo(() => {
    const arr = Array.isArray(subjectsRaw?.data) ? (subjectsRaw!.data as any[]) : [];
    return arr
      .filter((s: any) => !s.isDelete)
      .map((s: any) => ({ id: s.id as number, name: s.subject_name as string }));
  }, [subjectsRaw]);

  const stats: Stats = (statsData?.data as Stats) || { total: 0, this_month: 0, above_average: 0, below_average: 0, avg_percentage: 0 };
  const focuses: string[] = (focusesData?.data as string[]) || [];
  const listPayload: any = listData?.data;
  const rows: AssessmentRow[] = (listPayload?.rows || listPayload || []) as AssessmentRow[];

  const staffList = useMemo(
    () => ((teamData?.data as any[]) || []).map((t: any) => {
      const f = t.teamFirstName ?? t.first_name ?? "";
      const l = t.teamLastName ?? t.last_name ?? "";
      const full = `${f} ${l}`.trim();
      return { id: t.id, name: full || t.name || `Staff #${t.id}`, image: t.teamImg };
    }),
    [teamData]
  );
  const studentList = useMemo(
    () => ((studentData?.data as any[]) || []).map((s: any) => ({
      id: s.id,
      name: `${s.first_name || ""} ${s.last_name || ""}`.trim() || `Student #${s.id}`,
      image: s.image,
    })),
    [studentData]
  );

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<any>(emptyForm());
  const [editing, setEditing] = useState<AssessmentRow | null>(null);
  const [viewAssessment, setViewAssessment] = useState<AssessmentRow | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Cohort-level trend/distribution removed from this page — per-student
  // analytics belong on the Student Profile Assessments tab where all
  // records refer to one child. The top-students leaderboard below is
  // the only remaining consumer of the analytics endpoint.
  const { data: analyticsData } = useGetAssessmentAnalyticsQuery();
  const analytics: any = analyticsData?.data || {};
  const topStudents: { name: string; avg_percentage: number }[] = analytics.top_students || [];

  const [showFocusDrop, setShowFocusDrop] = useState(false);

  const [createAssessment, createState] = useCreateAssessmentMutation();
  const [updateAssessment, updateState] = useUpdateAssessmentMutation();
  const [deleteAssessment] = useDeleteAssessmentMutation();
  const confirm = useConfirm();

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  const applyFilters = () => setAppliedFilters(filters);
  const clearFilters = () => {
    const empty = { student_name: "", from: "", to: "", focus: "", staff_id: "", grade: "", subject: "", analysis: "", range: "" };
    setFilters(empty);
    setAppliedFilters(empty);
  };

  const openEdit = (a: AssessmentRow) => {
    setEditing(a);
    setForm({
      student_id: String(a.student_id),
      staff_id: String(a.staff_id),
      subject_id: (a as any).subject_id ?? "",
      subject: a.subject,
      title: a.title,
      focus: a.focus || "",
      grade: a.grade || "",
      max_value: a.max_value,
      student_mark: a.student_mark,
      date: a.date,
      comment_1: a.comment_1 || "",
      comment_2: a.comment_2 || "",
      comment_3: a.comment_3 || "",
    });
    setShowCreate(true);
    setViewAssessment(null);
  };

  const closeCreate = () => {
    setShowCreate(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const submit = async () => {
    if (!form.student_id || !form.staff_id || !form.subject || !form.title || !form.date) {
      setToast({ msg: "Please fill all required fields.", ok: false });
      return;
    }
    const body = {
      ...form,
      student_id: Number(form.student_id),
      staff_id: Number(form.staff_id),
      max_value: Number(form.max_value) || 1,
      student_mark: Math.min(Number(form.student_mark) || 0, Number(form.max_value) || 1),
    };
    try {
      if (editing) {
        await updateAssessment({ id: editing.id, body }).unwrap();
        setToast({ msg: "✅ Assessment updated", ok: true });
      } else {
        await createAssessment(body).unwrap();
        setToast({ msg: "✅ Assessment created", ok: true });
      }
      closeCreate();
      refetch();
      refetchStats();
    } catch (e: any) {
      setToast({ msg: e?.data?.message || "Failed to save assessment.", ok: false });
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm(confirmPresets.deletePermanent("assessment"));
    if (!ok) return;
    try {
      await deleteAssessment(id).unwrap();
      setToast({ msg: "Assessment deleted.", ok: true });
      setViewAssessment(null);
      refetch();
      refetchStats();
    } catch (e: any) {
      setToast({ msg: e?.data?.message || "Failed to delete.", ok: false });
    }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("token") || "";
    const params = new URLSearchParams();
    Object.entries(appliedFilters || {}).forEach(([k, v]) => {
      if (v) params.set(k, String(v));
    });
    // Fetch as blob so the Bearer token can be attached in a header.
    fetch(`${(window as any).REACT_APP_API_URL || process.env.REACT_APP_API_URL || ""}/assessments/export?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `assessments-${new Date().toISOString().split("T")[0]}.csv`; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setToast({ msg: "Failed to export.", ok: false }));
  };

  const pctColor = (p: number) => (p >= 80 ? "#16a34a" : p >= 50 ? "#d97706" : "#dc2626");
  const analysisPill = (p: number) => (p >= 80 ? "⭐ Above Avg" : p >= 50 ? "📊 Average" : "⚠️ Below Avg");
  const analysisBg = (p: number) => (p >= 80 ? "#f0fdf4" : p >= 50 ? "#fffbeb" : "#fef2f2");

  const gridCols = "36px 190px 100px 100px 1fr 170px 110px 110px 120px 130px 100px";

  return (
    <div style={{ background: "#f0f4f8", minHeight: "100vh", fontFamily: "Inter, sans-serif" }}>
      {/* HEADER */}
      <div style={{
        background: "linear-gradient(135deg, #1b3a6b 0%, #2a5298 100%)",
        padding: "28px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 4 }}>
            Academic Management
          </div>
          <h1 style={{ color: "#fff", fontSize: 26, fontWeight: 800, margin: 0 }}>📝 Assessments</h1>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, marginTop: 4 }}>
            Track student performance, marks, and progress
          </div>
          <div style={{
            color: "rgba(255,255,255,0.55)", fontSize: 12, marginTop: 10,
            display: "flex", gap: 14, flexWrap: "wrap",
          }}>
            <span>{stats.this_month} assessment{stats.this_month === 1 ? "" : "s"} this month</span>
            <span>·</span>
            <span>Avg score: {stats.avg_percentage || 0}%</span>
            {rows[0]?.date && (
              <>
                <span>·</span>
                <span>Last updated: {presentDateOnly(rows[0].date)}</span>
              </>
            )}
          </div>
        </div>
        {canCreate && (
          <button
            onClick={() => { setEditing(null); setForm(emptyForm()); setShowCreate(true); }}
            data-testid="assessments-new-btn"
            style={{
              background: "#fff", color: "#1b3a6b", border: "none",
              borderRadius: 14, padding: "14px 28px",
              fontWeight: 800, fontSize: 15, cursor: "pointer",
              boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >＋ New Assessment</button>
        )}
      </div>

      {/* STAT CARDS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, padding: "24px 32px" }}>
        {[
          { label: "Total Assessments", value: stats.total,        icon: "📋", color: "#1b3a6b", bg: "#eef2ff" },
          { label: "This Month",        value: stats.this_month,   icon: "📅", color: "#0891b2", bg: "#f0f9ff" },
          { label: "Above Average",     value: stats.above_average, icon: "⭐", color: "#16a34a", bg: "#f0fdf4" },
          { label: "Below Average",     value: stats.below_average, icon: "⚠️", color: "#dc2626", bg: "#fef2f2" },
          { label: "Avg Score",         value: `${stats.avg_percentage || 0}%`, icon: "📊", color: "#7c3aed", bg: "#f5f3ff" },
        ].map((c) => (
          <div key={c.label} style={{
            background: c.bg, borderRadius: 14, padding: "18px 20px",
            border: `1px solid ${c.color}22`,
            borderTop: `3px solid ${c.color}`,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{c.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: c.color }}>{c.value}</div>
            <div style={{
              fontSize: 10, fontWeight: 700, color: c.color, opacity: 0.7,
              letterSpacing: "0.6px", textTransform: "uppercase", marginTop: 4,
            }}>{c.label}</div>
          </div>
        ))}
      </div>


      {/* FILTERS */}
      <div style={{
        margin: "0 32px 20px", background: "#fff", borderRadius: 14,
        border: "1px solid #e2e8f0", padding: "16px 20px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 2, minWidth: 180 }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94a3b8" }}>🔍</span>
            <input
              value={filters.student_name}
              onChange={(e) => setFilters((p) => ({ ...p, student_name: e.target.value }))}
              placeholder="Search student name…"
              style={{
                width: "100%", padding: "9px 12px 9px 36px",
                border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: 13,
                outline: "none", boxSizing: "border-box", color: "#1a1a2e",
              }}
            />
          </div>
          <PremiumDatePicker
            value={filters.from}
            onChange={(v) => setFilters((p) => ({ ...p, from: v }))}
            placeholder="From date"
          />
          <PremiumDatePicker
            value={filters.to}
            onChange={(v) => setFilters((p) => ({ ...p, to: v }))}
            placeholder="To date"
          />

          <PremiumSelect
            value={filters.focus}
            onChange={(v) => setFilters((p) => ({ ...p, focus: v }))}
            placeholder="All Focus"
            emptyLabel="All Focus"
            options={focuses.map((f) => ({ value: f, label: f }))}
          />
          <PremiumSelect
            value={filters.staff_id}
            onChange={(v) => setFilters((p) => ({ ...p, staff_id: v }))}
            placeholder="All Staff"
            emptyLabel="All Staff"
            showAvatar
            options={staffList.map((s) => ({
              value: String(s.id), label: s.name, avatar: s.image || null,
            }))}
          />
          <PremiumSelect
            value={filters.grade}
            onChange={(v) => setFilters((p) => ({ ...p, grade: v }))}
            placeholder="All Grades"
            emptyLabel="All Grades"
            options={GRADES.map((g) => ({ value: g, label: g }))}
          />
          <PremiumSelect
            value={filters.subject}
            onChange={(v) => setFilters((p) => ({ ...p, subject: v }))}
            placeholder="All Subjects"
            emptyLabel="All Subjects"
            options={editSubjectList.map((s) => ({ value: s.name, label: s.name }))}
          />
          <PremiumSelect
            value={filters.analysis}
            onChange={(v) => setFilters((p) => ({ ...p, analysis: v }))}
            placeholder="All Analysis"
            emptyLabel="All Analysis"
            options={[
              { value: "Above Average", label: "Above Average" },
              { value: "Average",       label: "Average" },
              { value: "Below Average", label: "Below Average" },
            ]}
          />
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {(["Today", "Month", "Year"] as const).map((r) => {
            const key = r.toLowerCase();
            const active = filters.range === key;
            const activeBg = r === "Today" ? "#ef4444" : r === "Month" ? "#f59e0b" : "#3b82f6";
            return (
              <button
                key={r}
                onClick={() => setFilters((p) => ({ ...p, range: active ? "" : key }))}
                style={{
                  padding: "8px 20px", borderRadius: 8, border: "none",
                  fontWeight: 600, fontSize: 13, cursor: "pointer",
                  background: active ? activeBg : "#f1f5f9",
                  color: active ? "#fff" : "#64748b", transition: "all 0.15s",
                }}
              >{r}</button>
            );
          })}
          <button onClick={applyFilters} style={{
            padding: "8px 24px", borderRadius: 8, border: "none",
            background: "#7c3aed", color: "#fff",
            fontWeight: 700, fontSize: 13, cursor: "pointer",
            boxShadow: "0 4px 12px rgba(124,58,237,0.3)",
          }}>🔍 Filter</button>
          <button onClick={clearFilters} style={{
            padding: "8px 16px", borderRadius: 8,
            border: "1px solid #e2e8f0", background: "#fff",
            color: "#64748b", fontWeight: 600, fontSize: 13, cursor: "pointer",
          }}>✕ Clear</button>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button onClick={exportCsv} style={{
              padding: "8px 16px", borderRadius: 8,
              border: "1px solid #bbf7d0", background: "#f0fdf4",
              color: "#16a34a", fontWeight: 600, fontSize: 13, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 5,
            }}>📄 CSV</button>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div style={{
        margin: "0 32px 32px", background: "#fff", borderRadius: 16,
        border: "1px solid #e2e8f0", overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <div style={{
          background: "#1b3a6b", display: "grid",
          gridTemplateColumns: gridCols, padding: "13px 20px", gap: 12,
        }}>
          {["#", "Student", "Date", "Subject", "Title", "Staff", "Focus", "Grade", "Marks", "Analysis", "Actions"].map((h) => (
            <div key={h} style={{
              fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.85)",
              letterSpacing: "0.6px", textTransform: "uppercase",
            }}>{h}</div>
          ))}
        </div>

        {isFetching ? (
          <div style={{ padding: "60px 20px", textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: "60px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📝</div>
            <div style={{ fontWeight: 600, color: "#1b3a6b", fontSize: 16 }}>No assessments found</div>
            <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 6 }}>Create your first assessment or adjust the filters</div>
            {canCreate && (
              <button onClick={() => { setEditing(null); setForm(emptyForm()); setShowCreate(true); }} style={{
                marginTop: 16, background: "#1b3a6b", color: "#fff",
                border: "none", borderRadius: 10, padding: "12px 24px",
                fontWeight: 700, fontSize: 14, cursor: "pointer",
              }}>＋ Create Assessment</button>
            )}
          </div>
        ) : rows.map((a, i) => {
          const studentName = `${a.student?.first_name || ""} ${a.student?.last_name || ""}`.trim() || "Student";
          return (
            <div
              key={a.id}
              onClick={() => setViewAssessment(a)}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f4ff")}
              onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafafa")}
              style={{
                display: "grid", gridTemplateColumns: gridCols,
                padding: "16px 20px", gap: 12, alignItems: "center",
                background: i % 2 === 0 ? "#fff" : "#fafafa",
                borderBottom: "1px solid #f1f5f9",
                transition: "background 0.1s", cursor: "pointer",
              }}
            >
              {/* # rank / index */}
              <div style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8" }}>
                {i + 1}
              </div>

              {/* Student — avatar + name + grade */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <img
                  src={getAvatar(studentName, a.student?.image || null, 34)}
                  alt={studentName}
                  style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", border: "1.5px solid #e2e8f0", flexShrink: 0 }}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{studentName}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{a.grade}</div>
                </div>
              </div>

              {/* Date */}
              <div style={{ fontSize: 12, fontWeight: 600, color: "#1b3a6b" }}>
                {presentDateOnly(a.date)}
              </div>

              {/* Subject — vertically centered chip */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{
                  background: "#eff6ff", color: "#2563eb", borderRadius: 6,
                  padding: "4px 10px", fontSize: 11, fontWeight: 600,
                }}>{a.subject}</span>
              </div>

              {/* Title */}
              <div style={{
                fontSize: 12, color: "#1a1a2e", fontWeight: 500, minWidth: 0,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{a.title}</div>

              {/* Staff — avatar + name */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <img
                  src={getAvatar(a.staff?.name || "Staff", a.staff?.teamImg || null, 28)}
                  alt={a.staff?.name || ""}
                  style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                />
                <span style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {a.staff?.name}
                </span>
              </div>

              {/* Focus */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{
                  background: "#fdf4ff", color: "#7c3aed", borderRadius: 6,
                  padding: "4px 10px", fontSize: 11, fontWeight: 600,
                }}>{a.focus || "—"}</span>
              </div>

              {/* Grade */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{
                  background: "#f0fdf4", color: "#16a34a", borderRadius: 6,
                  padding: "4px 10px", fontSize: 11, fontWeight: 600,
                }}>{a.grade || "—"}</span>
              </div>

              {/* Marks — stacked pills centered */}
              <div style={{
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: 4,
              }}>
                <span style={{
                  background: "#1b3a6b", color: "#fff", borderRadius: 5,
                  padding: "3px 10px", fontSize: 11, fontWeight: 700, textAlign: "center",
                  minWidth: 68,
                }}>{a.student_mark}/{a.max_value}</span>
                <span style={{
                  background: "#7c3aed", color: "#fff", borderRadius: 5,
                  padding: "3px 10px", fontSize: 11, fontWeight: 700, textAlign: "center",
                  minWidth: 68,
                }}>{a.percentage}%</span>
              </div>

              {/* Analysis */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{
                  background: analysisBg(a.percentage), color: pctColor(a.percentage),
                  borderRadius: 8, padding: "5px 10px", fontSize: 11, fontWeight: 700,
                  border: `1px solid ${pctColor(a.percentage)}33`, whiteSpace: "nowrap",
                }}>{analysisPill(a.percentage)}</span>
              </div>

              {/* Actions — icon buttons */}
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}
              >
                <button
                  title="View"
                  onClick={() => setViewAssessment(a)}
                  style={rowIconBtn("#eff6ff", "#bfdbfe", "#2563eb")}
                >👁</button>
                {/* Grade — only for group-backed rows. Legacy standalone
                    rows (assessment_group_id null) have no roster; the
                    icon is hidden so a click can't reach a broken page. */}
                {a.assessment_group_id != null && (
                  <button
                    title="Grade"
                    onClick={() => navigate(`/assessments/groups/${a.assessment_group_id}/grade`)}
                    style={rowIconBtn("#f0fdf4", "#bbf7d0", "#16a34a")}
                  >📝</button>
                )}
                <button
                  title="Edit"
                  onClick={() => openEdit(a)}
                  style={rowIconBtn("#fffbeb", "#fde68a", "#d97706")}
                >✏️</button>
                <button
                  title="Delete"
                  onClick={() => handleDelete(a.id)}
                  style={rowIconBtn("#fef2f2", "#fecaca", "#dc2626")}
                >🗑</button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
        padding: 20, margin: "0 32px 32px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#1b3a6b", marginBottom: 4 }}>
          🏆 Top Performing Students
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
          Average assessment scores — top 10 students
        </div>
        {topStudents.length > 0 ? (
          <div>
            {topStudents.map((t: any, i: number) => {
              const stImg = studentList.find((x) => String(x.id) === String(t.student_id))?.image || null;
              const pct = t.avg_percentage;
              const barColor = pct >= 80 ? "#16a34a" : pct >= 50 ? "#f59e0b" : "#ef4444";
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "12px 0",
                  borderBottom: i < topStudents.length - 1 ? "1px solid #f1f5f9" : "none",
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: i === 0 ? "#ffd700" : i === 1 ? "#c0c0c0" : i === 2 ? "#cd7f32" : "#f1f5f9",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 800,
                    color: i < 3 ? "#fff" : "#94a3b8", flexShrink: 0,
                  }}>{i + 1}</div>
                  <img
                    src={getAvatar(t.name, stImg, 36)}
                    alt={t.name}
                    style={{
                      width: 36, height: 36, borderRadius: "50%",
                      objectFit: "cover", border: "2px solid #e2e8f0", flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 600, fontSize: 13, color: "#1a1a2e",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      {t.count} assessment{t.count !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div style={{ width: 140, flexShrink: 0 }}>
                    <div style={{ background: "#f1f5f9", borderRadius: 6, height: 8 }}>
                      <div style={{
                        width: `${Math.min(100, Math.max(0, pct))}%`,
                        height: 8, borderRadius: 6,
                        background: barColor, transition: "width 0.5s ease",
                      }} />
                    </div>
                  </div>
                  <div style={{
                    fontWeight: 800, fontSize: 15, minWidth: 48, textAlign: "right",
                    color: pct >= 80 ? "#16a34a" : pct >= 50 ? "#d97706" : "#dc2626",
                  }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{
            height: 120, display: "flex", alignItems: "center", justifyContent: "center",
            color: "#94a3b8", fontSize: 13, textAlign: "center",
          }}>
            Create assessments to see student rankings here
          </div>
        )}
      </div>

      {/* ── CREATE FLOW ── new two-step wizard, posts to /assessments/groups */}
      {showCreate && !editing && (
        <CreateAssessmentWizard
          open={showCreate}
          onClose={closeCreate}
          staffOptions={staffList}
          onCreated={(msg) => {
            setToast({ msg: msg || "Assessment created.", ok: true });
            refetch();
            refetchStats();
          }}
        />
      )}

      {/* ── EDIT MODAL ── legacy single-row edit stays intact for existing rows.
          Marks + report-card comments are still available here because an
          edit specifically targets a single already-existing assessment
          row; the new wizard is Create-only. */}
      {showCreate && editing && (
        <AssessmentModalShell
          open={showCreate}
          onClose={closeCreate}
          editing={!!editing}
          onSave={submit}
          saveDisabled={createState.isLoading || updateState.isLoading}
          saving={createState.isLoading || updateState.isLoading}
        >
          <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <PremiumDatePicker
                  label="DATE *"
                  value={form.date}
                  onChange={(v) => setForm((p: any) => ({ ...p, date: v }))}
                  placeholder="Select date"
                />
                <PremiumSelect
                  label="STUDENT *"
                  placeholder="Select Student"
                  showAvatar
                  value={String(form.student_id || "")}
                  onChange={(v) => setForm((p: any) => ({ ...p, student_id: v }))}
                  options={studentList.map((s) => ({
                    value: String(s.id), label: s.name, avatar: s.image || null,
                  }))}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <PremiumSelect
                  label="STAFF *"
                  placeholder="Select Staff"
                  showAvatar
                  value={String(form.staff_id || "")}
                  onChange={(v) => setForm((p: any) => ({ ...p, staff_id: v }))}
                  options={staffList.map((s) => ({
                    value: String(s.id), label: s.name, avatar: s.image || null,
                  }))}
                />
                <PremiumSelect
                  label="GRADE"
                  placeholder="Select Grade"
                  value={form.grade || ""}
                  onChange={(v) => setForm((p: any) => ({ ...p, grade: v }))}
                  options={GRADES.map((g) => ({ value: g, label: g }))}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle}>SUBJECT *</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                  {editSubjectList.length === 0 && (
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>
                      No subjects configured.
                    </div>
                  )}
                  {editSubjectList.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setForm((p: any) => ({ ...p, subject_id: s.id, subject: s.name }))}
                      style={{
                        height: 36, padding: "0 18px", borderRadius: 8, border: "none",
                        fontWeight: 600, fontSize: 13, cursor: "pointer",
                        background: Number(form.subject_id) === s.id ? "#1b3a6b" : "#f1f5f9",
                        color: Number(form.subject_id) === s.id ? "#fff" : "#64748b",
                        transition: "all 0.15s",
                      }}
                    >{s.name}</button>
                  ))}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div style={{ position: "relative" }}>
                  <label style={labelStyle}>FOCUS</label>
                  <input
                    value={form.focus || ""}
                    onChange={(e) => { setForm((p: any) => ({ ...p, focus: e.target.value })); setShowFocusDrop(true); }}
                    onFocus={() => setShowFocusDrop(true)}
                    onBlur={() => setTimeout(() => setShowFocusDrop(false), 150)}
                    placeholder="Select or type new focus…"
                    style={{ ...modalInputStyle, paddingRight: 32 }}
                  />
                  <span style={{
                    position: "absolute", right: 12, top: 38, fontSize: 11,
                    color: "#94a3b8", pointerEvents: "none",
                  }}>▼</span>
                  {showFocusDrop && (
                    <div style={{
                      position: "absolute", top: "100%", left: 0, right: 0, zIndex: 300,
                      background: "#fff", borderRadius: 10,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      border: "1px solid #e2e8f0", maxHeight: 200, overflowY: "auto",
                      marginTop: 4,
                    }}>
                      {form.focus && !focuses.includes(form.focus) && (
                        <div
                          onMouseDown={() => setShowFocusDrop(false)}
                          style={{
                            padding: "10px 14px", cursor: "pointer",
                            background: "#eff6ff", color: "#2563eb", fontWeight: 600,
                            fontSize: 13, borderBottom: "1px solid #f1f5f9",
                            display: "flex", alignItems: "center", gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 16 }}>＋</span>
                          Add "{form.focus}" as new focus
                        </div>
                      )}
                      {focuses
                        .filter((f) => f.toLowerCase().includes((form.focus || "").toLowerCase()))
                        .map((f) => (
                          <div
                            key={f}
                            onMouseDown={() => { setForm((p: any) => ({ ...p, focus: f })); setShowFocusDrop(false); }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = form.focus === f ? "#eff6ff" : "#fff")}
                            style={{
                              padding: "10px 14px", cursor: "pointer",
                              fontSize: 13, color: "#1a1a2e",
                              borderBottom: "1px solid #f8fafc",
                              background: form.focus === f ? "#eff6ff" : "#fff",
                            }}
                          >{f}</div>
                        ))}
                    </div>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>TITLE *</label>
                  <input value={form.title} placeholder="e.g. Mid-term Test"
                    onChange={(e) => setForm((p: any) => ({ ...p, title: e.target.value }))}
                    style={modalInputStyle} />
                </div>
              </div>

              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                gap: 16, marginBottom: 16, alignItems: "end",
              }}>
                <div>
                  <label style={labelStyle}>MAX VALUE *</label>
                  <input
                    type="number" min={1} value={form.max_value}
                    onChange={(e) => setForm((p: any) => ({ ...p, max_value: parseInt(e.target.value) || 100 }))}
                    style={numBoxStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>STUDENT MARK *</label>
                  <input
                    type="number" min={0} max={form.max_value}
                    value={form.student_mark}
                    onChange={(e) => setForm((p: any) => ({ ...p, student_mark: parseInt(e.target.value) || 0 }))}
                    style={numBoxStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>PERCENTAGE</label>
                  <div style={{
                    ...numBoxStyle,
                    background: "#f8fafc",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800,
                    color: form.max_value > 0
                      ? (form.student_mark / form.max_value * 100 >= 80 ? "#16a34a"
                        : form.student_mark / form.max_value * 100 >= 50 ? "#d97706"
                        : "#dc2626")
                      : "#94a3b8",
                  }}>
                    {form.max_value > 0
                      ? `${Math.round(form.student_mark / form.max_value * 100)}%`
                      : "0%"}
                  </div>
                </div>
              </div>

              <div style={{
                background: "#f8fafc", borderRadius: 12,
                padding: "14px 16px", marginBottom: 14,
                border: "1px solid #e2e8f0",
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: "#1b3a6b",
                  letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 12,
                }}>Report Card Comments (Optional)</div>
                {[
                  { key: "comment_1", label: "The student did well on…" },
                  { key: "comment_2", label: "They need to work on…" },
                  { key: "comment_3", label: "To improve they need to…" },
                ].map((c) => (
                  <div key={c.key} style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: "#64748b", display: "block", marginBottom: 4 }}>
                      {c.label}
                    </label>
                    <textarea
                      value={form[c.key] || ""}
                      onChange={(e) => setForm((p: any) => ({ ...p, [c.key]: e.target.value }))}
                      rows={2}
                      placeholder={c.label}
                      style={{
                        width: "100%", border: "1px solid #e2e8f0", borderRadius: 8,
                        padding: "8px 10px", fontSize: 12, resize: "vertical",
                        outline: "none", fontFamily: "inherit", boxSizing: "border-box", color: "#1a1a2e",
                      }}
                    />
                  </div>
                ))}
              </div>
          </>
        </AssessmentModalShell>
      )}

      {/* VIEW MODAL */}
      {viewAssessment && (
        <div style={overlayStyle} onClick={() => setViewAssessment(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 20, width: 560, maxWidth: "100%",
            maxHeight: "85vh", overflow: "auto", boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
          }}>
            <div style={{
              background: "linear-gradient(135deg,#1b3a6b,#2a5298)",
              padding: 24, textAlign: "center", position: "relative",
            }}>
              <button onClick={() => setViewAssessment(null)} style={{
                position: "absolute", top: 16, right: 16,
                background: "rgba(255,255,255,0.15)", border: "none",
                color: "#fff", width: 30, height: 30, borderRadius: 7,
                cursor: "pointer", fontSize: 16,
              }}>✕</button>
              <img
                src={getAvatar(
                  `${viewAssessment.student?.first_name || ""} ${viewAssessment.student?.last_name || ""}`.trim() || "Student",
                  viewAssessment.student?.image || null, 64
                )}
                style={{ width: 60, height: 60, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.4)", objectFit: "cover", marginBottom: 10 }}
              />
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>
                {viewAssessment.student?.first_name} {viewAssessment.student?.last_name}
              </div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, marginTop: 2 }}>
                {viewAssessment.subject} · {viewAssessment.title}
              </div>
              <div style={{
                marginTop: 20, display: "inline-flex", alignItems: "center", gap: 20,
                background: "rgba(255,255,255,0.1)", borderRadius: 16, padding: "16px 32px",
              }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 40, fontWeight: 900, color: "#fff" }}>{viewAssessment.student_mark}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>out of {viewAssessment.max_value}</div>
                </div>
                <div style={{ width: 1, height: 50, background: "rgba(255,255,255,0.2)" }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{
                    fontSize: 40, fontWeight: 900,
                    color: viewAssessment.percentage >= 80 ? "#86efac"
                         : viewAssessment.percentage >= 50 ? "#fde68a" : "#fca5a5",
                  }}>{viewAssessment.percentage}%</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>percentage</div>
                </div>
              </div>
            </div>

            <div style={{ padding: "20px 24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                {[
                  { label: "Date", value: viewAssessment.date },
                  { label: "Staff", value: viewAssessment.staff?.name },
                  { label: "Grade", value: viewAssessment.grade },
                  { label: "Focus", value: viewAssessment.focus },
                  { label: "Subject", value: viewAssessment.subject },
                  { label: "Analysis", value: viewAssessment.analysis },
                ].map((item) => (
                  <div key={item.label} style={{
                    background: "#f8fafc", borderRadius: 10,
                    padding: "12px 14px", border: "1px solid #e2e8f0",
                  }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: "#94a3b8",
                      textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4,
                    }}>{item.label}</div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}>{item.value || "—"}</div>
                  </div>
                ))}
              </div>

              {(viewAssessment.comment_1 || viewAssessment.comment_2 || viewAssessment.comment_3) && (
                <div style={{
                  background: "#f8fafc", borderRadius: 12,
                  padding: 16, border: "1px solid #e2e8f0",
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: "#1b3a6b",
                    letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 12,
                  }}>Report Card Comments</div>
                  {viewAssessment.comment_1 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#16a34a" }}>✅ Did well on:</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e", marginTop: 2 }}>{viewAssessment.comment_1}</div>
                    </div>
                  )}
                  {viewAssessment.comment_2 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#d97706" }}>🔧 Needs to work on:</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e", marginTop: 2 }}>{viewAssessment.comment_2}</div>
                    </div>
                  )}
                  {viewAssessment.comment_3 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#7c3aed" }}>📈 To improve:</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e", marginTop: 2 }}>{viewAssessment.comment_3}</div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button onClick={() => openEdit(viewAssessment)} style={{
                  flex: 1, background: "#eff6ff", color: "#2563eb",
                  border: "1px solid #bfdbfe", borderRadius: 10, padding: 11,
                  fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}>✏️ Edit</button>
                <button onClick={() => handleDelete(viewAssessment.id)} style={{
                  flex: 1, background: "#fef2f2", color: "#dc2626",
                  border: "1px solid #fecaca", borderRadius: 10, padding: 11,
                  fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}>🗑 Delete</button>
                <button
                  onClick={() => navigate(`/student-history/${viewAssessment.student_id}`)}
                  style={{
                    flex: 1, background: "#1b3a6b", color: "#fff",
                    border: "none", borderRadius: 10, padding: 11,
                    fontWeight: 700, fontSize: 13, cursor: "pointer",
                  }}
                >👤 Student Profile</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 500,
          background: toast.ok ? "#f0fdf4" : "#fef2f2",
          border: `1px solid ${toast.ok ? "#bbf7d0" : "#fecaca"}`,
          color: toast.ok ? "#16a34a" : "#991b1b",
          padding: "12px 20px", borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
          fontSize: 13, fontWeight: 600,
        }}>{toast.msg}</div>
      )}
    </div>
  );
};

// ─────────────── styles ─────────────── //
const labelStyle: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: "#64748b",
  textTransform: "uppercase", letterSpacing: "0.6px",
  display: "block", marginBottom: 6,
};
const inputStyle: CSSProperties = {
  width: "100%", padding: "10px 12px",
  border: "1.5px solid #e2e8f0", borderRadius: 9,
  fontSize: 13, outline: "none", color: "#1a1a2e",
  boxSizing: "border-box", fontFamily: "inherit",
  background: "#fff",
};
// Matches the visual height of PremiumSelect trigger (10px vertical
// padding + 14px content = ~42px) so modal rows line up on the baseline.
const rowIconBtn = (bg: string, border: string, color: string): CSSProperties => ({
  width: 28, height: 28, borderRadius: 7,
  background: bg, border: `1px solid ${border}`, color,
  cursor: "pointer", fontSize: 13, padding: 0,
  display: "flex", alignItems: "center", justifyContent: "center",
});
const modalInputStyle: CSSProperties = {
  width: "100%", height: 42, padding: "0 14px",
  border: "1.5px solid #e2e8f0", borderRadius: 10,
  fontSize: 13, outline: "none", color: "#1a1a2e",
  boxSizing: "border-box", fontFamily: "inherit", background: "#fff",
};
const numBoxStyle: CSSProperties = {
  width: "100%", padding: "11px 12px",
  border: "1.5px solid #e2e8f0", borderRadius: 10,
  fontSize: 15, fontWeight: 600, textAlign: "center",
  outline: "none", color: "#1a1a2e", boxSizing: "border-box",
  fontFamily: "inherit", background: "#fff",
  MozAppearance: "textfield" as any,
};
// Filter-bar date input — 38px high to match the row's other controls.
const filterDateStyle: CSSProperties = {
  height: 38, padding: "0 12px",
  border: "1.5px solid #e2e8f0", borderRadius: 9,
  fontSize: 13, outline: "none", color: "#1a1a2e",
  background: "#fff", cursor: "pointer", fontFamily: "inherit",
  boxSizing: "border-box",
};
const dateInputStyle: CSSProperties = {
  padding: "9px 12px", border: "1.5px solid #e2e8f0",
  borderRadius: 9, fontSize: 13, outline: "none",
  color: "#1a1a2e", background: "#fff", cursor: "pointer",
};
const filterSelect: CSSProperties = {
  border: "1.5px solid #e2e8f0", borderRadius: 9,
  padding: "9px 12px", fontSize: 13, outline: "none",
  color: "#1a1a2e", background: "#fff",
  cursor: "pointer", minWidth: 120,
};
const overlayStyle: CSSProperties = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.5)", zIndex: 400,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

export default Assessments;
